import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import useAgentsChats from '../useAgentsChats';
import { TextFieldWithButtonV2 } from '../../common/ai-chat/input-atoms/TextFieldWithButtonV2';
import { useSelector, useDispatch } from 'react-redux';
import { selectActiveChatId } from '../agentsChatsReducer';
import { useAgentChatInstance } from '../AgentChatInstanceContext';
import AgentInputModeSelector from './AgentInputModeSelector';
import { AgentInputMode } from './agentInputModeTypes';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { selectPendingInputText, clearPendingInputText } from '../agentsHomeReducer';

const AgentInput = ({
  onConfirm,
  isLoading,
  disabled,
  selectedText,
  onClearSelectedText,
  placeholderText,
  renderStopButton,
  onStopClick,
  menuDirection = 'down',
  disableInputModeSelector
}: {
  onConfirm: (
    message: string,
    options?: {
      advancedMode?: AgentInputMode;
      additionalOrgIds?: string[];
    },
  ) => Promise<void>;
  isLoading: boolean;
  disabled: boolean;
  selectedText?: string | null;
  onClearSelectedText?: () => void;
  placeholderText?: string;
  renderStopButton?: boolean;
  onStopClick?: () => void;
  menuDirection?: 'up' | 'down';
  disableInputModeSelector?:boolean
}) => {
  const [value, setValue] = useState('');
  const dispatch = useDispatch();
  const { chatInstanceId } = useAgentChatInstance();
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const pendingInputText = useSelector(selectPendingInputText);
  // Ref to store pending input text to survive chat switches
  const pendingInputRef = useRef<string | undefined>(undefined);
  const {
    inputPlaceholder: placeholderTextByType,
    advancedMode,
    handleModeChange,
    enableAdvancedMode,
    selectedAdditionalOrgIds,
  } = useAgentsChats();
  const { multiOrgQuery: isMultiOrgQueryEnabled, agentResearchMode: isAgentResearchModeEnabled } =
    useFeatureToggle();

  // Store pending input text in a ref when it arrives (before navigation completes)
  useEffect(() => {
    if (pendingInputText) {
      pendingInputRef.current = pendingInputText;
      dispatch(clearPendingInputText());
    }
  }, [pendingInputText, dispatch]);

  // Reset the input when switching chats, but apply pending input if available
  useEffect(() => {
    if (pendingInputRef.current) {
      setValue(pendingInputRef.current);
      pendingInputRef.current = undefined;
    } else {
      setValue('');
    }
  }, [activeChatId]);

  // Reset modes if their feature flags are turned off
  useEffect(() => {
    if (advancedMode === AgentInputMode.MultiOrgMode && !isMultiOrgQueryEnabled) {
      handleModeChange(AgentInputMode.None);
    } else if (advancedMode === AgentInputMode.DeepDiveMode && !isAgentResearchModeEnabled) {
      handleModeChange(AgentInputMode.None);
    }
  }, [advancedMode, isMultiOrgQueryEnabled, isAgentResearchModeEnabled, handleModeChange]);

  const leftAdornment = useMemo(() => {
    if (!enableAdvancedMode || disableInputModeSelector) return undefined;
    return (
      <AgentInputModeSelector
        selectedMode={advancedMode}
        onModeChange={handleModeChange}
        menuDirection={menuDirection}
      />
    );
  }, [enableAdvancedMode, advancedMode, handleModeChange, menuDirection, disableInputModeSelector]);

  const handleConfirm = useCallback(
    async (message: string) => {
      // Pass additionalOrgIds only when in multi-org mode and orgs are selected
      const additionalOrgIds =
        advancedMode === AgentInputMode.MultiOrgMode && selectedAdditionalOrgIds.length > 0
          ? selectedAdditionalOrgIds
          : undefined;
      await onConfirm(message, { advancedMode, additionalOrgIds });
    },
    [onConfirm, advancedMode, selectedAdditionalOrgIds],
  );

  return (
    <TextFieldWithButtonV2
      value={value}
      setValue={setValue}
      onConfirm={handleConfirm}
      inputPlaceholder={placeholderText ?? placeholderTextByType}
      isLoading={isLoading}
      readonly={disabled}
      selectedText={selectedText}
      onClearSelectedText={onClearSelectedText}
      maxInputHeight={400}
      renderStopButton={renderStopButton}
      onStopClick={onStopClick}
      leftAdornment={leftAdornment}
      leftAdornmentOnSeparateLine={advancedMode !== AgentInputMode.None}
    />
  );
};

export default AgentInput;
